import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { apiFetch } from "@/lib/supabase";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BarChart3, TrendingUp, XCircle, UserX } from "lucide-react";

export default function BookingInsights() {
  const { currentOrgId } = useAuth();
  const [range, setRange] = useState("this_month");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!currentOrgId) return;
    setLoading(true);
    apiFetch(`/scheduling/insights?organization_id=${currentOrgId}&range_=${range}`)
      .then(setData).catch(() => setData(null)).finally(() => setLoading(false));
  }, [currentOrgId, range]);

  const maxDaily = useMemo(() => {
    if (!data?.series?.length) return 1;
    return Math.max(1, ...data.series.map((s) => s.count));
  }, [data]);

  return (
    <div className="bg-white rounded-2xl border border-[#E8E2D9] p-5" data-testid="booking-insights">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <BarChart3 size={16} className="text-[#E8712C]" strokeWidth={1.75} />
          <h3 className="font-display font-semibold text-base">Booking Insights</h3>
        </div>
        <Select value={range} onValueChange={setRange}>
          <SelectTrigger className="w-40 h-8 text-xs" data-testid="insights-range"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="this_week">This Week</SelectItem>
            <SelectItem value="this_month">This Month</SelectItem>
            <SelectItem value="last_30_days">Last 30 Days</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading && <p className="text-xs text-[#A8A29E]">Loading…</p>}
      {data && (
        <>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mb-4">
            {[
              { label: "Total", val: data.counts.total, testid: "kpi-total" },
              { label: "Confirmed", val: data.counts.confirmed, testid: "kpi-confirmed" },
              { label: "Completed", val: data.counts.completed, testid: "kpi-completed" },
              { label: "Cancelled", val: data.counts.cancelled, testid: "kpi-cancelled" },
              { label: "No-show", val: data.counts.no_show, testid: "kpi-noshow" },
              { label: "Pending", val: data.counts.pending, testid: "kpi-pending" },
            ].map((c) => (
              <div key={c.label} className="text-center p-2 rounded-lg bg-[#FAF8F5]" data-testid={c.testid}>
                <div className="text-lg font-display font-bold">{c.val}</div>
                <div className="text-[10px] text-[#57534E] uppercase tracking-wider">{c.label}</div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
            <div className="p-3 rounded-xl border border-[#E8E2D9]" data-testid="cancellation-rate">
              <div className="flex items-center gap-1.5 mb-1"><XCircle size={13} className="text-[#B02222]" />
                <span className="text-xs uppercase tracking-wider text-[#57534E]">Cancellation Rate</span></div>
              <div className="text-2xl font-display font-bold">{data.cancellation_rate_pct}%</div>
              <div className="text-xs text-[#A8A29E]">of {data.cancellation_denominator} eligible bookings</div>
            </div>
            <div className="p-3 rounded-xl border border-[#E8E2D9]" data-testid="noshow-rate">
              <div className="flex items-center gap-1.5 mb-1"><UserX size={13} className="text-[#B02222]" />
                <span className="text-xs uppercase tracking-wider text-[#57534E]">No-Show Rate</span></div>
              <div className="text-2xl font-display font-bold">{data.no_show_rate_pct}%</div>
              <div className="text-xs text-[#A8A29E]">of {data.no_show_denominator} past bookings</div>
            </div>
          </div>

          {data.series?.length > 0 && (
            <div className="mb-3" data-testid="over-time-chart">
              <div className="text-xs uppercase tracking-wider text-[#57534E] mb-2 flex items-center gap-1.5">
                <TrendingUp size={12} /> Bookings over time
              </div>
              <div className="flex items-end gap-0.5 h-24">
                {data.series.map((s) => (
                  <div key={s.date} className="flex-1 flex flex-col items-center justify-end min-w-[4px]"
                       title={`${s.date}: ${s.count}`}>
                    <div className="w-full bg-[#E8712C] rounded-sm" style={{
                      height: `${Math.max(3, (s.count / maxDaily) * 100)}%`, opacity: s.count === 0 ? 0.15 : 0.85,
                    }} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {data.by_appointment_type?.length > 0 && (
            <div data-testid="by-type-table">
              <div className="text-xs uppercase tracking-wider text-[#57534E] mb-2">By appointment type</div>
              <div className="space-y-1">
                {data.by_appointment_type.slice(0, 6).map((t) => {
                  const pct = data.counts.total ? (t.count / data.counts.total) * 100 : 0;
                  return (
                    <div key={t.name} className="flex items-center gap-2 text-xs">
                      <div className="w-32 truncate">{t.name}</div>
                      <div className="flex-1 h-2 bg-[#FAF8F5] rounded-full overflow-hidden">
                        <div className="h-full bg-[#E8712C]" style={{ width: `${pct}%` }} />
                      </div>
                      <div className="w-8 text-right font-medium">{t.count}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {data.excluded_test_clients > 0 && (
            <p className="text-[10px] text-[#A8A29E] mt-3 italic">Excluding {data.excluded_test_clients} test client{data.excluded_test_clients === 1 ? "" : "s"}.</p>
          )}
        </>
      )}
    </div>
  );
}
